import * as Field from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import type { CreateSetup } from '@hvf/contracts'
import { type FormStore, getValue, setValue } from '@modular-forms/solid'
import { Crosshair } from 'lucide-solid'
import { For, Show, createEffect, createSignal } from 'solid-js'
import { Flex, Grid } from 'styled-system/jsx'
import * as v from 'valibot'
import { FieldGroupContainer } from '../components/FieldGroupContainer'
import type { FormFieldType } from '../index'
import { ComputedSchema } from '../schema/createSetupSchema'


export default function Computed(props: {
  FormField: FormFieldType
  form: FormStore<CreateSetup, undefined>
}) {
  const [isValid, setIsValid] = createSignal(false)

  createEffect(() => {
    const target = getValue(props.form, 'target')
    const axis = getValue(props.form, 'axis')
    const interim1 = getValue(props.form, 'interim_1')
    const interim2 = getValue(props.form, 'interim_2')
    const position = getValue(props.form, 'position')
    const riskRewardRatio = getValue(props.form, 'risk_reward_ratio')
    const timeStop = getValue(props.form, 'time_stop')
    const patternSpecification = getValue(props.form, 'pattern_specification')

    const validateResult = v.safeParse(ComputedSchema, {
      axis: axis,
      target: target,
      interim_1: interim1,
      interim_2: interim2,
      position: position,
      risk_reward_ratio: riskRewardRatio,
      time_stop: timeStop,
      pattern_specification: patternSpecification,
    })

    setIsValid(validateResult.success)
  })

  const formFields: Array<keyof CreateSetup> = ['interim_1', 'interim_2', 'target']

  return (
    <FieldGroupContainer
      heading="Computed"
      valid={isValid()}
    >
      <Grid
        gridTemplateColumns={{
          base: '1fr 1fr 1fr',
          lgDown: '1fr 1fr',
          mdDown: '1fr',
        }}
        gap={6}
        p={1}
        pointerEvents="none"
      >
        <props.FormField name="pattern_specification">
          {(field, formFieldProps) => (
            <Field.Root invalid={!!field.error}>
              <Field.Label>Pattern Specification</Field.Label>
              <Input
                {...formFieldProps}
                fontSize="14px"
                type="text"
                value={field.value ?? ''}
              />
              <Field.ErrorText>
                {field.error && <div>{field.error}</div>}
              </Field.ErrorText>
            </Field.Root>
          )}
        </props.FormField>

        <props.FormField
          name="risk_reward_ratio"
          type="number"
        >
          {(field, formFieldProps) => {
            return (
              <Field.Root invalid={!!field.error}>
                <Field.Label>Risk Reward Ratio</Field.Label>
                <Input
                  {...formFieldProps}
                  type="number"
                  onChange={(event: any) => {
                    const val = event.target.value;
                    setValue(props.form, 'risk_reward_ratio', val === '' ? undefined : Number(val))
                  }}
                  value={field.value ?? ''}
                />
                <Field.ErrorText>
                  {field.error && <div>{field.error}</div>}
                </Field.ErrorText>
              </Field.Root>
            )
          }}
        </props.FormField>

        <props.FormField
          name="axis"
          type="number"
        >
          {(field, formFieldProps) => (
            <Field.Root invalid={!!field.error}>
              <Field.Label>Axis</Field.Label>
              <Input
                {...formFieldProps}
                type="number"
                onChange={(event: any) => {
                  const val = event.target.value;
                  setValue(props.form, 'axis', val === '' ? undefined : Number(val))
                }}
                value={field.value ?? ''}
              />
              <Field.ErrorText>
                {field.error && <div>{field.error}</div>}
              </Field.ErrorText>
            </Field.Root>
          )}
        </props.FormField>

        <For each={formFields}>
          {(formField) => (
            <props.FormField
              name={formField}
              type="number"
            >
              {(field, formFieldProps) => (
                <Field.Root invalid={!!field.error}>
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Field.Label textTransform='capitalize'>{formField.replaceAll('_', ' ')}</Field.Label>
                  </Flex>
                  <Input
                    {...formFieldProps}
                    type="number"
                    onChange={(event: any) => {
                      const val = event.target.value;
                      setValue(props.form, formField, val === '' ? undefined : Number(val))
                    }}
                    value={field.value ?? '' as any}
                  />
                  <Field.ErrorText>
                    {field.error && <div>{field.error}</div>}
                  </Field.ErrorText>
                </Field.Root>
              )}
            </props.FormField>
          )}
        </For>
      </Grid>
    </FieldGroupContainer>
  )
}
